import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  ResponsiveContainer, RadarChart, Radar, PolarGrid, PolarAngleAxis, PolarRadiusAxis
} from 'recharts';
import { fetchShotHistory, fetchMachineOverview, fetchLstmDefectAnalysis } from '../../../services/api';
import { useAutoRefresh } from '../../../hooks/useAutoRefresh';
import { useTranslation } from '../../../context/LanguageContext';
import LSTMDefectAnalysis from '../../../components/LSTMDefectAnalysis';
import './ShotJudgeNew.css';

/* ===== 품질·생산 > 샷 판정 — PNG 시안「전사 > 베트남 공장 > 사출기 #1 > 품질 생산 > 샷 판정」 =====
 * 검수사진 탭(InspectionPhotos)의 서브 탭으로 렌더된다. 스타일은 sj- 접두어만 쓴다.
 * 구성: 사출정보(도면·제품·셋팅값) / Shot History(최근 N샷 표, 불량만 보기) / 설정값 vs 실측값 레이더 / 선택 샷 실측값.
 * "LSTM 불량 원인 분석" 버튼을 누르면 시안 2장째처럼 레이더가 넓어지고 오른쪽에 인라인 LSTM 패널(핵심 3종)이 열린다.
 * 패널의 "AI 분석 자세히 보기"는 구 양/불 탭에 있던 전체 LSTM 분석 화면(components/LSTMDefectAnalysis)을 이 탭 안에 펼친다.
 *
 * 데이터 출처
 * - 샷 표·실측값 : /machine/shot-history/{id}?limit=N — chargePosi1(계량) injStartPosi(사출) temp1Current(히터)
 *                  injSpeed1(속도) injEndPosi(쿠션) injTime(시간) qualityStatus(GOOD/BAD/DEFECT) craftId
 * - 셋팅값       : /dashboard/machine/{id}/overview — metering.position, injection.startPosition/speed/time,
 *                  heater.zones[Zone 1].setTemp. 쿠션량은 설정 항목이 없어 최근 10샷 평균(추정)을 기준으로 쓴다.
 * - LSTM         : /lstm/analyze/{id}/{craftId}?shot_number= — feature_importance / recommendations / confidence
 * - 제품번호      : 저장하는 필드가 없어 '—'. 제품 도면은 설비별 정적 이미지(기존 양/불 탭과 같은 파일)가 있을 때만 표시.
 *
 * "규격 이탈"(적색 셀·레이더 축 경고)은 불러온 샷의 평균 ±3σ 를 벗어난 값이다(화면 계산 · 추정). 값이 null 이면 '—'. */

const isNum = (v) => v != null && !Number.isNaN(Number(v));
const fmt = (v, digits = 1) => (isNum(v) ? Number(v).toFixed(digits) : '—');

/* 기존 양/불 탭과 같은 설비별 제품 도면. 매핑이 없으면 공용 도면 이미지를 쓰고, 그것도 없으면 아이콘만 보인다 */
const PRODUCT_IMAGE_BY_EQUIPMENT = { 45: '/product-drawing-e057.png' };
const productImage = (equipmentId) => PRODUCT_IMAGE_BY_EQUIPMENT[String(equipmentId)] || '/product-blueprint.png';

const LIMIT_OPTIONS = [50, 100, 200, 300];

/* 비교 항목 6개 — 샷 필드 ↔ 설정값 필드 ↔ 번역 키 ↔ 단위 */
const PARAMS = [
  { key: 'metering', shotField: 'chargePosi1', unit: 'mm', digits: 1, colKey: 'colMetering', axisKey: 'axisMetering', labelKey: 'mMetering' },
  { key: 'injection', shotField: 'injStartPosi', unit: 'mm', digits: 1, colKey: 'colInjection', axisKey: 'axisInjection', labelKey: 'mInjection' },
  { key: 'heater', shotField: 'temp1Current', unit: '℃', digits: 0, colKey: 'colHeater', axisKey: 'axisHeater', labelKey: 'mHeater' },
  { key: 'speed', shotField: 'injSpeed1', unit: 'mm/s', digits: 0, colKey: 'colSpeed', axisKey: 'axisSpeed', labelKey: 'mSpeed' },
  { key: 'cushion', shotField: 'injEndPosi', unit: 'mm', digits: 1, colKey: 'colCushion', axisKey: 'axisCushion', labelKey: 'mCushion' },
  { key: 'time', shotField: 'injTime', unit: 's', digits: 2, colKey: 'colTime', axisKey: 'axisTime', labelKey: 'mTime' }
];

const COLORS = { setting: '#47CD89', measured: '#83A6FF', grid: 'rgba(197,215,224,0.35)', tick: '#C5D7E0' };

const judgeOf = (qualityStatus) => {
  if (qualityStatus === 'GOOD') return 'good';
  if (qualityStatus === 'BAD' || qualityStatus === 'DEFECT') return 'bad';
  if (qualityStatus === 'WARN') return 'warn';
  return 'none';
};

/* overview 응답에서 항목별 설정값을 뽑는다. 값이 없으면 null (화면 '—' 또는 추정 대체) */
const settingFromOverview = (overview, key) => {
  if (!overview) return null;
  switch (key) {
    case 'metering': return overview.metering?.position;
    case 'injection': return overview.injection?.startPosition;
    case 'speed': return overview.injection?.speed;
    case 'time': return overview.injection?.time;
    case 'heater': {
      const zones = overview.heater?.zones || [];
      const zone1 = zones.find((z) => /1\b/.test(String(z?.zoneName || ''))) || zones[0];
      return zone1?.setTemp;
    }
    default: return null;   /* cushion: 설정 항목 없음 */
  }
};

const mean = (vals) => (vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : null);
const stdev = (vals, m) => (vals.length > 1 ? Math.sqrt(vals.reduce((a, v) => a + (v - m) ** 2, 0) / (vals.length - 1)) : 0);

function Badge({ tone, children, className = '' }) {
  return <span className={`sj-badge sj-badge-${tone} ${className}`}>{children}</span>;
}

/* ---- 인라인 LSTM 패널 (핵심 3종: 인자 중요도 / 튜닝 추천값 / 신뢰도) ---- */
function LstmPanel({ equipmentId, craftId, shotNumber, onClose, onDetail, tr }) {
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    if (!equipmentId || !craftId) { setState({ loading: false, error: 'noCraft', data: null }); return undefined; }
    let cancelled = false;
    setState({ loading: true, error: null, data: null });
    fetchLstmDefectAnalysis(equipmentId, craftId, shotNumber)
      .then((data) => { if (!cancelled) setState({ loading: false, error: null, data }); })
      .catch((e) => { console.error('Shot judge LSTM load failed:', e); if (!cancelled) setState({ loading: false, error: 'failed', data: null }); });
    return () => { cancelled = true; };
  }, [equipmentId, craftId, shotNumber]);

  const { loading, error, data } = state;
  const importance = (data?.feature_importance || []).slice(0, 5);          /* [{feature, feature_kr, importance(%), rank}] */
  const recommendations = (data?.recommendations || []).slice(0, 5);        /* [{feature_kr, current_value, recommended_value, direction}] */
  const confidence = isNum(data?.confidence?.overall) ? Number(data.confidence.overall) : null;
  const top = importance[0];
  const level = confidence == null ? null : confidence >= 80 ? 'levelHigh' : confidence >= 60 ? 'levelMid' : 'levelLow';

  const arrow = (rec) => {
    const cur = Number(rec.current_value);
    const next = Number(rec.recommended_value);
    if (!isNum(cur) || !isNum(next) || cur === next) return '';
    return next < cur ? ' ↓' : ' ↑';
  };

  return (
    <section className="sj-panel sj-lstm">
      <div className="sj-panel-head">
        <h3 className="sj-panel-title">{tr('lstmTitle')}</h3>
        <button type="button" className="sj-icon-btn" onClick={onClose} title={tr('close')} aria-label={tr('close')}>
          <span className="material-symbols-outlined">close</span>
        </button>
      </div>

      {loading ? <div className="sj-message">{tr('lstmLoading')}</div>
        : error === 'noCraft' ? <div className="sj-message">{tr('lstmNoCraft')}</div>
        : error ? <div className="sj-message sj-message-error">{tr('lstmFailed')}</div>
        : (
          <div className="sj-lstm-body">
            {/* 1. 불량 원인 인자 중요도 */}
            <h4 className="sj-lstm-sub">{tr('lstmImportance')}</h4>
            {importance.length === 0 ? <div className="sj-message sj-message-sm">{tr('lstmNoData')}</div> : (
              <ul className="sj-importance">
                {importance.map((item, idx) => {
                  const pct = isNum(item.importance) ? Math.max(0, Math.min(100, Number(item.importance))) : 0;
                  const tone = idx === 0 ? 'error' : idx === 1 ? 'warning' : 'muted';
                  return (
                    <li key={item.feature || idx}>
                      <span className="sj-importance-label">{item.feature_kr || item.feature}</span>
                      <span className="sj-importance-track"><i className={`sj-importance-fill sj-fill-${tone}`} style={{ width: `${pct}%` }} /></span>
                      <span className="sj-importance-pct">{Math.round(pct)}%</span>
                    </li>
                  );
                })}
              </ul>
            )}
            {top && isNum(top.importance) && (
              <div className="sj-lstm-note">{tr('lstmDominant', { f: top.feature_kr || top.feature, p: Math.round(Number(top.importance)) })}</div>
            )}

            {/* 2. 공정 튜닝 추천값 */}
            <div className="sj-lstm-sub-row">
              <h4 className="sj-lstm-sub">{tr('lstmRecommend')}</h4>
              <button type="button" className="sj-btn-primary sj-btn-sm" onClick={onDetail}>{tr('lstmDetail')}</button>
            </div>
            {recommendations.length === 0 ? <div className="sj-message sj-message-sm">{tr('lstmNoData')}</div> : (
              <table className="sj-rec-table">
                <thead>
                  <tr><th>{tr('colParam')}</th><th>{tr('colCurrent')}</th><th>{tr('colRecommended')}</th></tr>
                </thead>
                <tbody>
                  {recommendations.map((rec, idx) => (
                    <tr key={rec.feature || idx}>
                      <td>{rec.feature_kr || rec.feature}</td>
                      <td>{fmt(rec.current_value, 2)}{rec.unit ? ` ${rec.unit}` : ''}</td>
                      <td>{fmt(rec.recommended_value, 2)}{rec.unit ? ` ${rec.unit}` : ''}{arrow(rec)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}

            {/* 3. 모델 예측 신뢰도 */}
            <h4 className="sj-lstm-sub">{tr('lstmConfidence')}</h4>
            {confidence == null ? <div className="sj-message sj-message-sm">{tr('lstmNoData')}</div> : (
              <>
                <div className="sj-confidence">
                  <span className="sj-importance-label">{tr('confidence')}</span>
                  <span className="sj-importance-track"><i className="sj-importance-fill sj-fill-success" style={{ width: `${Math.min(100, confidence)}%` }} /></span>
                  <b className="sj-confidence-pct">{Math.round(confidence)}%</b>
                </div>
                <div className="sj-lstm-note">{tr('confidenceNote', { p: Math.round(confidence), lv: tr(level) })}</div>
              </>
            )}
          </div>
        )}
    </section>
  );
}

function ShotJudgeNew({ equipmentId }) {
  const { t } = useTranslation();
  const tr = useCallback((key, params) => t(`pages.monitoring.machine.shotJudge.${key}`, params), [t]);

  const [limit, setLimit] = useState(100);
  const [defectOnly, setDefectOnly] = useState(false);
  const [shots, setShots] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedShot, setSelectedShot] = useState(null);   /* shotCount */
  const [imageFailed, setImageFailed] = useState(false);

  /* LSTM 패널 · 전체 분석 화면은 브라우저 기록(history state)에 남긴다.
   * 같은 /machine 주소로 기록을 하나 추가하므로 브라우저 뒤로가기가 자세히 보기 → 인라인 패널 → 샷 판정 순으로 한 단계씩 돌아간다.
   * 기존 state(machineData · openTab)는 그대로 넘겨 사출기 선택이 바뀌지 않게 한다. */
  const location = useLocation();
  const navigate = useNavigate();
  const lstmView = location.state?.shotJudgeLstm;   /* undefined | 'open' | 'detail' */
  const lstmOpen = lstmView === 'open';
  const lstmDetail = lstmView === 'detail';
  const pushLstmView = (view) => navigate(`${location.pathname}${location.search}`, { state: { ...location.state, shotJudgeLstm: view } });
  const closeLstmView = () => navigate(-1);

  const load = useCallback(async (silent = false) => {
    if (!equipmentId) return;
    if (!silent) { setLoading(true); setError(null); }
    const [shotRes, overviewRes] = await Promise.allSettled([
      fetchShotHistory(equipmentId, limit),
      fetchMachineOverview(equipmentId)
    ]);
    if (shotRes.status === 'fulfilled') setShots(shotRes.value?.shots || []);
    else { console.error('Shot judge history load failed:', shotRes.reason); if (!silent) setError(tr('loadFailed')); }
    if (overviewRes.status === 'fulfilled') setOverview(overviewRes.value);
    else console.error('Shot judge overview load failed:', overviewRes.reason);
    if (!silent) setLoading(false);
  }, [equipmentId, limit, tr]);

  useEffect(() => { load(false); }, [load]);
  useAutoRefresh(() => load(true), !!equipmentId);
  useEffect(() => { setSelectedShot(null); setImageFailed(false); }, [equipmentId]);

  /* 사출기를 바꾸면 열려 있던 LSTM 화면을 닫는다 (기록을 더 쌓지 않도록 현재 항목을 교체) */
  const prevEquipmentId = useRef(equipmentId);
  useEffect(() => {
    if (prevEquipmentId.current === equipmentId) return;
    prevEquipmentId.current = equipmentId;
    if (location.state?.shotJudgeLstm) {
      const rest = { ...location.state };
      delete rest.shotJudgeLstm;
      navigate(`${location.pathname}${location.search}`, { replace: true, state: rest });
    }
  }, [equipmentId, location, navigate]);

  /* 항목별 평균·σ (불러온 샷 전체 기준) → 규격 이탈 판정(추정) */
  const stats = useMemo(() => {
    const out = {};
    PARAMS.forEach((p) => {
      const vals = shots.map((s) => Number(s[p.shotField])).filter((v) => Number.isFinite(v));
      const m = mean(vals);
      out[p.key] = { mean: m, sd: m == null ? 0 : stdev(vals, m), avg10: mean(vals.slice(0, 10)) };
    });
    return out;
  }, [shots]);

  const isOut = useCallback((key, value) => {
    const st = stats[key];
    if (!st || st.mean == null || !isNum(value) || !(st.sd > 0)) return false;
    return Math.abs(Number(value) - st.mean) > 3 * st.sd;
  }, [stats]);

  /* 셋팅값: overview 우선, 쿠션량은 최근 10샷 평균(추정) */
  const settings = useMemo(() => {
    const out = {};
    PARAMS.forEach((p) => {
      const v = settingFromOverview(overview, p.key);
      if (isNum(v)) out[p.key] = { value: Number(v), estimated: false };
      else if (p.key === 'cushion' && isNum(stats.cushion?.avg10)) out[p.key] = { value: stats.cushion.avg10, estimated: true };
      else out[p.key] = { value: null, estimated: false };
    });
    return out;
  }, [overview, stats]);

  const visibleShots = useMemo(
    () => (defectOnly ? shots.filter((s) => judgeOf(s.qualityStatus) === 'bad') : shots),
    [shots, defectOnly]
  );

  /* 선택 샷: 클릭한 샷이 목록에 있으면 그것, 없으면 표의 첫 샷(최신) */
  const current = useMemo(() => {
    const picked = selectedShot != null ? visibleShots.find((s) => s.shotCount === selectedShot) : null;
    return picked || visibleShots[0] || null;
  }, [visibleShots, selectedShot]);

  const currentJudge = current ? judgeOf(current.qualityStatus) : null;
  const craftId = current?.craftId || shots[0]?.craftId || '';

  /* 레이더: 설정값 = 70 고정, 실측값 = 70 + 편차/범위 × 30 (범위 = 3σ, σ 가 0 이면 설정값의 10%) */
  const radarRows = useMemo(() => PARAMS.map((p) => {
    const set = settings[p.key]?.value;
    const v = current ? Number(current[p.shotField]) : NaN;
    let measured = null;
    if (isNum(set) && Number.isFinite(v)) {
      const sd = stats[p.key]?.sd || 0;
      const range = sd > 0 ? 3 * sd : Math.max(Math.abs(set) * 0.1, 0.01);
      measured = Math.max(5, Math.min(100, 70 + ((v - set) / range) * 30));
    }
    return { axis: tr(p.axisKey), setting: isNum(set) ? 70 : null, measured };
  }), [settings, current, stats, tr]);

  const outAxes = useMemo(
    () => (current ? PARAMS.filter((p) => isOut(p.key, current[p.shotField])).map((p) => tr(p.axisKey)) : []),
    [current, isOut, tr]
  );

  const judgeLabel = (j) => tr(j);
  const judgeTone = (j) => (j === 'good' ? 'success' : j === 'bad' ? 'error' : j === 'warn' ? 'warning' : 'muted');

  /* 높이는 CSS(화면 맞춤)가 패널 남은 높이로 정한다. height 를 주면 그 값으로 고정 */
  const radar = (height) => (
    <div className="sj-radar" style={height ? { height } : undefined}>
      <ResponsiveContainer width="100%" height="100%">
        <RadarChart data={radarRows} outerRadius="72%">
          <PolarGrid stroke={COLORS.grid} />
          <PolarAngleAxis dataKey="axis" tick={{ fill: COLORS.tick, fontSize: 13 }} />
          <PolarRadiusAxis angle={90} domain={[0, 100]} tick={false} axisLine={false} />
          <Radar name={tr('settingLegend')} dataKey="setting" stroke={COLORS.setting} fill={COLORS.setting} fillOpacity={0.35} strokeWidth={1.5} isAnimationActive={false} />
          <Radar name={tr('measuredLegend')} dataKey="measured" stroke={COLORS.measured} fill={COLORS.measured} fillOpacity={0.3} strokeWidth={1.5} isAnimationActive={false} />
        </RadarChart>
      </ResponsiveContainer>
    </div>
  );

  const legend = (
    <div className="sj-legend">
      <span><i style={{ background: COLORS.setting }} />{tr('settingLegend')}</span>
      <span><i style={{ background: COLORS.measured }} />{tr('measuredLegend')}</span>
    </div>
  );

  const specNote = !current ? <span className="sj-spec-note">{tr('noSelection')}</span>
    : outAxes.length > 0 ? <span className="sj-spec-note sj-spec-out">{tr('outOfSpec', { axes: outAxes.join('·') })}</span>
    : <span className="sj-spec-note sj-spec-ok">{tr('inSpec')}</span>;

  /* ---- 전체 LSTM 분석 화면 (구 양/불 탭의 상세 분석) ---- */
  if (lstmDetail) {
    return (
      <div className="sj-root sj-root-detail">
        <div className="sj-detail-bar">
          <button type="button" className="sj-btn-back" onClick={closeLstmView}>
            <span className="material-symbols-outlined">arrow_back</span>{t('pages.monitoring.machine.passFail.back')}
          </button>
        </div>
        <div className="sj-detail-body">
          <LSTMDefectAnalysis
            selectedShotNumber={current?.shotCount}
            shotHistoryData={shots}
            equipmentId={equipmentId}
            craftId={craftId}
          />
        </div>
      </div>
    );
  }

  /* ---- LSTM 열림: 넓은 레이더 + 인라인 패널 ---- */
  if (lstmOpen) {
    return (
      <div className="sj-root sj-root-lstm">
        <section className="sj-panel">
          <div className="sj-panel-head">
            <h3 className="sj-panel-title">{tr('compareTitle')}</h3>
            {legend}
          </div>
          {radar()}
          <div className="sj-spec-box">{specNote}</div>
        </section>
        <LstmPanel
          equipmentId={equipmentId}
          craftId={craftId}
          shotNumber={current?.shotCount}
          onClose={closeLstmView}
          onDetail={() => pushLstmView('detail')}
          tr={tr}
        />
      </div>
    );
  }

  return (
    <div className="sj-root">
      {/* ---- 사출정보 ---- */}
      <section className="sj-panel sj-info">
        <h3 className="sj-panel-title">{tr('injectionInfo')}</h3>
        <div className="sj-drawing">
          {!imageFailed
            ? <img src={productImage(equipmentId)} alt={tr('drawingCaption', { id: craftId || '—' })} onError={() => setImageFailed(true)} />
            : <span className="material-symbols-outlined sj-drawing-icon">image</span>}
          <span className="sj-drawing-caption">{imageFailed ? tr('noImage') : tr('drawingCaption', { id: craftId || '—' })}</span>
        </div>
        <dl className="sj-rows">
          <div><dt>{tr('productNumber')}</dt><dd>—</dd></div>                       {/* 제품번호: 저장 필드 없음 */}
          <div><dt>{tr('productName')}</dt><dd>{craftId || '—'}</dd></div>           {/* shot-history.craftId */}
        </dl>
        <div className="sj-divider" />
        <div className="sj-sub-head">
          <h4 className="sj-sub-title">{tr('settingsTitle')}</h4>
          <Badge tone="accent">{tr('settingsBadge')}</Badge>
        </div>
        <dl className="sj-rows sj-rows-setting">
          {PARAMS.map((p) => {
            const s = settings[p.key];
            return (
              <div key={p.key}>
                <dt>{tr(p.labelKey)}</dt>
                <dd>
                  {s.estimated && <em className="sj-tag">{tr('estimated')}</em>}
                  {fmt(s.value, p.digits)}{isNum(s.value) && <small>{p.unit}</small>}
                </dd>
              </div>
            );
          })}
        </dl>
        <p className="sj-footnote">{tr('settingsNote')}<br />{tr('settingsEstimatedNote')}</p>
      </section>

      {/* ---- Shot History ---- */}
      <section className="sj-panel sj-history">
        <div className="sj-panel-head">
          <h3 className="sj-panel-title">{tr('shotHistory')}</h3>
          <div className="sj-controls">
            <select className="sj-select" value={limit} onChange={(e) => setLimit(Number(e.target.value))}>
              {LIMIT_OPTIONS.map((n) => <option key={n} value={n}>{tr('recentN', { n })}</option>)}
            </select>
            <label className={`sj-check-btn ${defectOnly ? 'active' : ''}`}>
              <input type="checkbox" checked={defectOnly} onChange={(e) => setDefectOnly(e.target.checked)} />
              <span className="sj-check-box"><span className="material-symbols-outlined">check</span></span>
              {tr('defectOnly')}
            </label>
          </div>
        </div>

        {loading && shots.length === 0 ? <div className="sj-message">{tr('loading')}</div>
          : error && shots.length === 0 ? <div className="sj-message sj-message-error">{error}</div>
          : visibleShots.length === 0 ? <div className="sj-message">{tr('empty')}</div>
          : (
            <div className="sj-table-wrap">
              <table className="sj-table">
                <thead>
                  <tr>
                    <th>{tr('colShot')}</th>
                    {PARAMS.map((p) => <th key={p.key}>{tr(p.colKey)}</th>)}
                    <th>{tr('colJudge')}</th>
                  </tr>
                </thead>
                <tbody>
                  {visibleShots.map((s) => {
                    const j = judgeOf(s.qualityStatus);
                    const active = current && s.shotCount === current.shotCount;
                    return (
                      <tr key={`${s.shotCount}-${s.createdAt || ''}`} className={active ? 'sj-row-active' : ''} onClick={() => setSelectedShot(s.shotCount)}>
                        <td className="sj-td-shot">#{s.shotCount}</td>
                        {PARAMS.map((p) => (
                          <td key={p.key} className={isOut(p.key, s[p.shotField]) ? 'sj-td-out' : ''}>{fmt(s[p.shotField], p.digits)}</td>
                        ))}
                        <td><Badge tone={judgeTone(j)}>{judgeLabel(j)}</Badge></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        <p className="sj-footnote">{tr('tableNote')}</p>
      </section>

      {/* ---- 설정값 vs 실측값 비교 ---- */}
      <section className="sj-panel sj-compare">
        <div className="sj-panel-head">
          <div className="sj-title-row">
            <h3 className="sj-panel-title">{tr('compareTitle')}</h3>
            {current && <Badge tone={judgeTone(currentJudge)}>{tr('shotBadge', { n: current.shotCount, j: judgeLabel(currentJudge) })}</Badge>}
          </div>
          {legend}
        </div>
        {radar()}
        <div className="sj-compare-foot">
          {specNote}
          <button type="button" className="sj-btn-primary" onClick={() => pushLstmView('open')} disabled={!current}>
            <span className="sj-btn-star">✦</span>{tr('lstmButton')}
          </button>
        </div>
      </section>

      {/* ---- 선택 샷 실측값 ---- */}
      <section className="sj-panel sj-measured">
        <div className="sj-panel-head">
          <h3 className="sj-panel-title">{tr('measuredTitle', { n: current ? current.shotCount : '—' })}</h3>
          {current && (
            <span className="sj-judge-inline">
              <span className="sj-judge-label">{tr('judgeLabel')}</span>
              <Badge tone={judgeTone(currentJudge)}>{judgeLabel(currentJudge)}</Badge>
            </span>
          )}
        </div>
        <div className="sj-measured-grid">
          {PARAMS.map((p) => {
            const v = current ? current[p.shotField] : null;
            const out = current ? isOut(p.key, v) : false;
            return (
              <div key={p.key} className={`sj-measured-card ${out ? 'sj-measured-out' : ''}`}>
                <span className="sj-measured-label">{tr(p.labelKey)}</span>
                <span className="sj-measured-value">{fmt(v, p.digits)}{isNum(v) && <small>{p.unit}</small>}</span>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

export default ShotJudgeNew;
